import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LazyLoad from "@/components/trip-builder/image/LazyLoad";
import { formatPrice, roundUpToWholeNumber } from "@/lib/general";
import { HeartIcon } from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import {
  AccommodationRecommendation,
  ContentItem,
} from "@/stores/tripBuilder/useToraStore";

// Create adapter to convert AccommodationRecommendation to ContentItem format
export const accommodationToContentItem = (
  accommodation: AccommodationRecommendation
): ContentItem => {
  return {
    name: accommodation.name,
    description: `${accommodation.roomType} - ${accommodation.address}`,
    types: [
      `${accommodation.starRating}-star hotel`,
      accommodation.includeBreakfast ? "Breakfast included" : "",
      accommodation.freeWifi ? "Free Wi-Fi" : "",
    ].filter(Boolean),
    image: accommodation.image,
    id: accommodation.id,
  };
};

interface AccommodationCardProps {
  accommodation: AccommodationRecommendation;
  onSelect: () => void;
  isAdmin?: boolean;
}

const AccommodationCard: React.FC<AccommodationCardProps> = ({
  accommodation,
  onSelect,
  isAdmin = false,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLiked, setIsLiked] = useState(false);

  const handleLike = () => {
    setIsLiked(!isLiked);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 3000);
  };

  return (
    <div className="w-full rounded-md mb-2">
      <div className="relative w-full">
        <button
          onClick={onSelect}
          className="rounded-md h-48 w-full overflow-hidden relative"
        >
          <LazyLoad
            src={accommodation.image.url}
            lowResSrc={accommodation.image.url}
            alt={accommodation.name}
            className="w-full h-full object-cover"
          />
        </button>

        {/* Price overlay */}
        <div className="absolute top-2 left-2 flex flex-row items-center space-x-1 bg-white/80 backdrop-blur-md rounded-md px-2 py-1">
          <p className="text-xs font-light text-neutral-900">
            {formatPrice(
              accommodation.currency,
              roundUpToWholeNumber(accommodation.dailyRate)
            )}
          </p>
        </div>

        {/* Like button */}
        <div className="group z-10 absolute right-3 top-2 flex items-center justify-center bg-stone-100 w-[27px] h-[27px] rounded-full">
          <button
            onClick={handleLike}
            className="group relative flex items-center justify-center w-full h-full"
          >
            <AnimatePresence>
              {isLiked && (
                <motion.i
                  key="filled-heart"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="text-[14px] text-red-600 absolute"
                >
                  <HeartIconSolid className="w-4 h-4" />
                </motion.i>
              )}
            </AnimatePresence>
            <motion.div
              animate={isLiked ? { scale: 0 } : { scale: 1 }}
              className={`${
                isLiked ? "text-transparent" : "text-gray-600"
              } transition-colors duration-300`}
            >
              <HeartIcon className="w-4 h-4" />
            </motion.div>
            {isAnimating && (
              <motion.div
                initial={{ scale: 0.8, opacity: 1 }}
                animate={{ scale: 1.3, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 bg-red-500/75 rounded-full"
              />
            )}
          </button>
        </div>

        {/* Flying hearts animation */}
        <AnimatePresence>
          {isAnimating && (
            <div className="absolute right-3 top-2 w-[35px] h-[35px] overflow-visible pointer-events-none">
              {[...Array(5)].map((_, index) => (
                <motion.i
                  key={index}
                  initial={{
                    opacity: 1,
                    scale: 0,
                    x: "50%",
                    y: "50%",
                  }}
                  animate={{
                    opacity: 0,
                    scale: 1.5,
                    x: `${100 + (Math.random() - 0.5) * 100}%`,
                    y: `${-50 - Math.random() * 100}%`,
                  }}
                  transition={{
                    duration: 1.5 + Math.random() * 1,
                    ease: "easeOut",
                    opacity: { duration: 2 },
                  }}
                  className="fa-solid fa-heart text-red-600 text-sm absolute"
                  style={{
                    originX: "50%",
                    originY: "50%",
                  }}
                />
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>

      <div className="flex flex-col w-full pt-2 pb-0">
        <div className="w-full">
          <h3 className="text-[15px] w-full font-[500] tracking-tight text-neutral-800">
            {truncateTitle(accommodation.name, 32)}
          </h3>
        </div>

        <div className="flex flex-row items-center justify-between w-full mt-1">
          <div className="flex flex-row items-center space-x-1.5">
            <div className="py-1 bg-indigo-50 flex flex-row items-center space-x-2 px-2 text-indigo-600 rounded-md">
              <i className="fa-solid fa-star text-[11px]"></i>
              <p className="text-[13px] font-light">
                {accommodation.reviewScore}
              </p>
            </div>
            <p className="pl-0.5 text-[12px] font-light text-neutral-500">
              {formatReviewCount(accommodation.reviewCount)} reviews
            </p>
          </div>

          {/* <div className="flex space-x-2">
            {isAdmin && (
              <button
                onClick={onSelect}
                className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-indigo-800 hover:bg-indigo-700 font-medium transition-all duration-500 ease-in-out text-white"
              >
                <i className="fa-solid fa-plus text-xs"></i>
              </button>
            )}
            <button
              onClick={onSelect}
              className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 font-medium transition-all duration-500 ease-in-out text-neutral-800"
            >
              <p className="text-xs font-bold italic">i</p>
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
};

const formatReviewCount = (count: number) => {
  if (count < 1000) {
    return count;
  } else {
    return (count / 1000).toFixed(1) + "k";
  }
};

const truncateTitle = (title: string, maxLength: number) => {
  if (title.length > maxLength) {
    return title.slice(0, maxLength) + "...";
  }
  return title;
};

export default AccommodationCard;
